import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type {
  ChartImageExporter,
  ExportImageOptions,
} from 'components/chart-export';

/**
 * Which content-export checklist item a registered chart feeds:
 *  - `chartsVisuals` — a Charts & Visuals chart (SciChart); exported WYSIWYG.
 *  - `reserves` — the Reserves Category Evolution chart (Highcharts); always exported.
 *  - `productionVolumes` — the Production Volumes chart (Highcharts); always exported (its
 *    section is kept mounted, CSS-hidden when its Layout toggle is off, so it stays capturable).
 *  - `cashFlow` — the Cash Flow chart (Highcharts); always exported (its section is kept
 *    mounted, CSS-hidden when its Layout toggle is off, so it stays capturable).
 *  - `valueRollup` — the Output Aggregation Visual Summary chart (SciChart). Its section
 *    is kept mounted, rendered off-screen when the Layout toggle is off, so this SciChart
 *    surface stays live and capturable (a `display:none` SciChart chart captures blank).
 *  - `map` — the Master Data map (Mapbox). Captured WYSIWYG from the live map canvas, so it's
 *    exported only when the map is shown (the checklist item disables when it isn't).
 */
export type ChartExportGroup =
  | 'chartsVisuals'
  | 'reserves'
  | 'productionVolumes'
  | 'cashFlow'
  | 'valueRollup'
  | 'map';

/**
 * One capturable chart's imperative handle. `exportToBlob` renders it to a PNG/JPEG
 * Blob at the given options (the same `ChartImageExporter.exportToBlob` from the
 * shared chart-export toolkit); `id` is the ZIP `charts[key]` name.
 */
export interface ChartExportEntry {
  id: string;
  name: string;
  group: ChartExportGroup;
  exportToBlob: (opts: ExportImageOptions) => Promise<Blob | null>;
}

/**
 * The registry API — imperative on purpose (see `ChartExportRegistryProvider`): the
 * content-export flow PULLS `getEntries()` at click time; charts push handles in/out
 * on mount/unmount. No reactive subscription.
 */
export interface ChartExportRegistry {
  register: (entry: ChartExportEntry) => void;
  unregister: (id: string) => void;
  getEntries: () => ChartExportEntry[];
}

const ChartExportRegistryContext = createContext<ChartExportRegistry | null>(
  null
);

/**
 * Holds the live set of capturable charts for the content-export feature. Deliberately
 * a **ref-backed `Map`, not reactive state**: the exporter handles are imperative (they
 * close over live chart surfaces), and the only reader — the export flow — needs them
 * once, at Export-click time (a pull, not a subscription). A `Map` in a ref mutated on
 * each chart's mount/unmount is O(1) and triggers no re-render; the context value is
 * memoized on the three stable callbacks so consumers never re-render on registry churn.
 *
 * Mounted once (headless) at the eval3-new work-area root, wrapping every section whose
 * charts can be exported.
 *
 * @param {object} props - `children` — the subtree whose charts register here.
 * @return {JSX.Element} The provider.
 */
export function ChartExportRegistryProvider({
  children,
}: {
  children: ReactNode;
}) {
  const entriesRef = useRef<Map<string, ChartExportEntry>>(new Map());

  const register = useCallback((entry: ChartExportEntry) => {
    entriesRef.current.set(entry.id, entry);
  }, []);

  const unregister = useCallback((id: string) => {
    entriesRef.current.delete(id);
  }, []);

  // Read at Export-click time, never memoized by the caller (charts can mount/unmount
  // while the modal is open) — always returns the current set.
  const getEntries = useCallback(() => [...entriesRef.current.values()], []);

  const value = useMemo<ChartExportRegistry>(
    () => ({ register, unregister, getEntries }),
    [register, unregister, getEntries]
  );

  return (
    <ChartExportRegistryContext.Provider value={value}>
      {children}
    </ChartExportRegistryContext.Provider>
  );
}

/**
 * Reads the chart-export registry. Throws outside `ChartExportRegistryProvider` so a
 * provider-placement bug surfaces immediately. Only eval3-new-local components (which
 * always render inside the provider) use this — the shared chart components expose their
 * exporter via a callback prop instead, so they never depend on this eval3-new context.
 *
 * @return {ChartExportRegistry} The registry API.
 * @throws {Error} When used outside a `ChartExportRegistryProvider`.
 */
export function useChartExportRegistry(): ChartExportRegistry {
  const ctx = useContext(ChartExportRegistryContext);
  if (ctx === null) {
    throw new Error(
      'useChartExportRegistry must be used inside a ChartExportRegistryProvider'
    );
  }
  return ctx;
}

/** Identifies a chart to the registry: `id` is the ZIP `charts[key]` name. */
export interface ChartExportDescriptor {
  id: string;
  name: string;
  group: ChartExportGroup;
}

/**
 * Bridges a chart component's `onExporterReady(exporter | null)` callback to the
 * registry: returns a stable handler that registers the chart's `exportToBlob` when
 * an exporter arrives (surface/chart ready) and unregisters it on teardown (null).
 *
 * The chart engines expose their exporter via a callback prop rather than importing
 * this eval3-new context, so this hook is the eval3-new-local adapter that owns the
 * registry coupling. **Provider-tolerant**: outside a `ChartExportRegistryProvider`
 * (e.g. a chart rendered standalone in a unit test) it no-ops instead of throwing, so
 * charts stay usable off the eval3-new page. Pass `null`/`undefined` to opt a chart
 * out of registration entirely (the returned handler then does nothing).
 *
 * @param {ChartExportDescriptor | null | undefined} descriptor - The chart's id/name/group, or nullish to skip registration.
 * @return {Function} An `onExporterReady(exporter | null)` handler for a chart view.
 */
export function useRegisterChartExport(
  descriptor: ChartExportDescriptor | null | undefined
): (exporter: Pick<ChartImageExporter, 'exportToBlob'> | null) => void {
  const registry = useContext(ChartExportRegistryContext);
  const descriptorRef = useRef(descriptor);
  descriptorRef.current = descriptor;

  return useCallback(
    (exporter: Pick<ChartImageExporter, 'exportToBlob'> | null) => {
      const current = descriptorRef.current;
      if (!registry || !current) return;
      // A ready exporter must be able to produce a Blob (the upload payload); if it
      // can't (or it's a teardown), drop the entry so the registry only holds usable charts.
      if (exporter?.exportToBlob) {
        registry.register({
          id: current.id,
          name: current.name,
          group: current.group,
          exportToBlob: exporter.exportToBlob,
        });
      } else {
        registry.unregister(current.id);
      }
    },
    [registry]
  );
}
